import { DeliveryMethodCodes } from "~/assets/constants/ecomApi";
import type { ProductAvailability } from "../components/atoms/TsRadioTrolleyCollection.vue";

import { useErrorHandler } from "~/composables/useErrorHandler";
import EcomTrolleyService from "~/services/ecom.trolley.service";
import type { APIState } from "~/types/api-state.type";
import type { Product } from "~/types/ecom/product/product.type";
import type { ProductStockResponse } from "~/types/ecom/product/stock.type";
import {
  TrolleyChannel,
  TrolleyStatus,
} from "~/types/ecom/trolley/trolley-map";
import type {
  AppliedPromoCodeResource,
  TrolleyDetails,
  TrolleyDiscountResponse,
  TrolleyItemUpdateRequest,
  TrolleyLineItem,
  TrolleyLineItemCore,
  TrolleyLineRequest,
  TrolleyResponse,
} from "~/types/ecom/trolley/trolley.type";
import EcomCustomerService from "~/services/ecom/customer.service";

export type RefinedStockResponse = Record<
    "delivery" | "collection",
    Array<Omit<ProductStockResponse, "site_id">> | null
>;

/**
 * @example
 */
export type RefinedTrolleyLineItems = Record<
    Exclude<keyof typeof TrolleyChannel, "SameDayDelivery">,
    TrolleyLineItem[]
>;

export type TrolleyItemVariant = Product & {
  quantity: number;
  channel: TrolleyChannel;
};

export const useTrolleyStore = defineStore("trolleyStore", {
  state: () => ({
    // monetate trolley item confirmation
    successMessage: useTranslation("Trolley-emailSuccessMessage", "Email sent successfully! Please check your inbox."),
    failureMessage: useTranslation("Trolley-emailFailureMessage", "Unable to send email"),
    current_add_on_item: <Product | null>null,
    trolley_modal_primary_tag: '',
    // states defined below are for the new PLP collection delivery flow
    previewed_product_v2: <Product | null>null,
    preview_overlay_collection_visible: false,

    preview_overlay_delivery_visible: false,
    already_subscribed_modal_visible: false,

    // states for variant overlays
    product_variants_modal_visible: false,
    delivery_variants_modal_visible: false,
    collection_variants_modal_visible: false,
    preview_collection_delivery_modal_visible: false,
    multiple_items_confirmation_modal_visible: false,

    active_trolley_cta_v1: <"AddToTrolley" | "BuyNow" | "BothCTA" | "">"",
    activeTrolleyCTAInModal: "",
    currentTrolleyAction: <
        "Delivery" | "Collection" | "BothCTA" | "NextDayCollection" | ""
        >"",

    delivery_product_variants: <TrolleyItemVariant[]>[],
    collection_product_variants: <TrolleyItemVariant[]>[],
    variant_items_added_to_trolley: <TrolleyItemVariant[]>[],

    trolley: <TrolleyDetails>{},
    total_products: 0,
    final_order_total_amount: "",
    trolley_id: <string | null>null,
    last_active_trolley_id: <string | null>null,
    trolley_session_id: <string | null>null,
    trolley_session_token: <string | null>null,
    promo_code_text: <string>"",
    applied_promo_codes: <AppliedPromoCodeResource[]>[],
    trolley_discount_data: <TrolleyDiscountResponse | null>null,
    is_promo_code_apply_button_loading: false,

    trolley_code_product: <TrolleyItemVariant[]>[],
    trolley_code: "",
    // shared states between two flows
    deleteConfirmationLineId: <number | null>null,
    previewed_product: <Product | null>null,
    previewed_product_selected_quantity: 1,
    previewed_product_selected_channel: <TrolleyChannel>1,
    previewed_product_variations: <Product[]>[],
    collection_availability: <ProductAvailability | undefined>undefined,
    collection_stock_loading: false,
    free_ship_buffer: 40,
    share_by_email_state: <APIState>{
      status: "idle",
    },

    // To display channel wise line items in the trolley page
    trolley_line_items: <RefinedTrolleyLineItems | null>null,

    loading: false,
    is_updating_trolley: false,
    is_decoding_trolley_code: true,
    add_to_trolley_cta_loading: false,
    buy_now_cta_loading: false,
    is_updating_item_details_in_add_to_trolley: false,
    order_summary_loading: false,
    is_added: false,
    // PLP page modals
    preview_overlay_visible: false,
    confirmation_overlay_visible: false,
    buy_now_overlay_visible: false,
    // Trolley page modals
    remove_confirmation_modal_visible: false,
    code_share_modal_visible: false,
    move_all_to_savelist_modal_visible: false,
    promoSuccess: {
      modalVisible: false,
      message: "",
      lastQualifiedPromoCode: ""
    },
  }),
  persist: [
    {
      pick: [
        "trolley_id",
        "last_active_trolley_id",
        "total_products",
        "trolley_session_id",
        "trolley_session_token",
      ],
    },
    {
      pick: ["trolley"],
      storage: import.meta.client ? localStorage : undefined,
    },
  ],
  getters: {
    is_empty(state): boolean {
      return (
          !state.trolley_id ||
          !state.trolley_line_items ||
          state.total_products === 0
      );
    },
    all_trolley_line_items(state): TrolleyLineItem[] {
      if (!state.trolley_line_items) return [];
      return Object.values(state.trolley_line_items).flat();
    },
    total_discount_amount(state): number {
      if(!state.trolley_discount_data || !state.trolley_discount_data.discount_total) return 0;
      return state.trolley_discount_data.discount_total / 100;
    }
  },
  actions: {
    isCheckoutDisabled() {
      if (!this.trolley_line_items) return false;

      const isAnyLineOutOfStock = Object.values(this.trolley_line_items)
          .flatMap((item) => item)
          .some((line) =>
              useStockStore().isTrolleyItemOutOfStock({
                channel: TrolleyChannel[line.channel] as any,
                product: line.product!,
                quantity: line.quantity,
              })
          );
      const isAnyLineProcessing = Object.values(this.trolley_line_items)
          .flatMap((item) => item)
          .some((line) => line.processing);

      const branchStore = useBranchStore();

      const isNextDayItemUnavailable =
          !branchStore.is_branch_set &&
          this.trolley_line_items.NextDayCollection.length > 0;

      return (
          isAnyLineOutOfStock ||
          isAnyLineProcessing ||
          isNextDayItemUnavailable ||
          this.loading ||
          this.order_summary_loading
      );
    },

    setTotalProducts(trolleyData: TrolleyResponse | null) {
      if (!trolleyData || !trolleyData.lines || !trolleyData.lines.length) {
        this.total_products = 0;
        return;
      }

      let sum = 0;

      trolleyData.lines.forEach((line) => {
        sum += line.quantity;
      });

      this.total_products = sum;
    },

    /**
     * @param {TrolleyResponse} trolleyData trolley resource from ecom
     */
    async setPersistingLineData(trolleyData: TrolleyResponse) {
      if (!trolleyData || !trolleyData.lines || !trolleyData.lines.length) {
        this.trolley.lines = [];
        return;
      }
      this.trolley.lines = trolleyData.lines.map(trolleyLine => {
        const { id, product_code, quantity, channel } = trolleyLine;
        return ({ id, product_code, quantity, channel });
      });
    },

    /**
     * @param trolleyData - Ecom trolley response
     * @type {RefinedTrolleyLineItems}
     */
    async setTrolleyLineItems(trolleyData: TrolleyResponse,
                              withEstimatedTimeDelivery = true) {
      const { setTrolleyLineItems } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await setTrolleyLineItems(trolleyData, withEstimatedTimeDelivery);                
    },

    /**
     * @param checkDiscount defaults to true and parallely checks if trolley is eligible for discounts
     * @description To set the order total summary with complete breakdown
     */
    async setOrderTotals(checkDiscount = true) {
      const { setOrderTotals } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await setOrderTotals(checkDiscount); 
    },

    /* To set an individual line to processing state */
    setTrolleyLineProcessing(line_id: number, processing: boolean) {
      if (!this.trolley_line_items) return;

      Object.keys(this.trolley_line_items).forEach((channelKey) => {
        const channel = channelKey as keyof RefinedTrolleyLineItems;
        const lineItem = this.trolley_line_items![channel].find(
            (line) => line.id === line_id
        );
        if(!lineItem) return;
        lineItem.processing = processing;
      });
    },

    async evaluateCollectionAvailability(
        product: Product,
        requiredQuantity = 1,
        reCalculate = false
    ): Promise<ProductAvailability> {
      const { useBranchStore } = await import("~/stores/branch.store");
      const branchStore = useBranchStore();
      // Branch not selected yet
      if (!branchStore.is_branch_set)
        return {
          status: "NoBranchSelected",
          stock: 0,
        };

      let productToBeProcessed = product;

      const { useStockStore } = await import("~/stores/stock.store");
      const stockStore = useStockStore();
      // if stock details are not available
      if (reCalculate) {
        const [productWithStock] = await stockStore.fetchStockProduct([
          product.code,
        ], [productToBeProcessed]);
        productToBeProcessed = productWithStock
      }

      // update stock of the product
      this.previewed_product = productToBeProcessed;

      return checkCollectionAvailability({ productToBeProcessed, requiredQuantity });
    },

    async createTrolley() {
      const { createTrolley: createNewTrolley } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await createNewTrolley();
    },

    /* Convert a guest trolley to Customer trolley */
    async convertToCustomer(canSetOtherDetails = true) {
      const { convertToCustomer } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await convertToCustomer(canSetOtherDetails);
    },

    async updateExistingTrolleyItem(
        existingTrolleyLineItem: TrolleyLineItemCore,
        requestOptions: TrolleyItemUpdateRequest,
        withEstimatedTimeDelivery = true
    ) {
      const { updateExistingTrolleyItem } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await updateExistingTrolleyItem(existingTrolleyLineItem, requestOptions, withEstimatedTimeDelivery);
    },

    /* Primary method responsible for add to trolley action */
    async dispatchItemToTrolley(
        product_code: Product["code"],
        quantity: number = 1,
        channel: TrolleyChannel = 1,
        delivery_method_code: DeliveryMethodCodes = DeliveryMethodCodes.Delivery,
        withEstimatedTimeDelivery = true,
        show_success_notification = false,

    ) {
      const { dispatchItemToTrolley } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      return await dispatchItemToTrolley(
        product_code,
        quantity,
        channel,
        delivery_method_code,
        withEstimatedTimeDelivery,
        show_success_notification,
      );
    },

    async addToTrolley(
        product_code: Product["code"],
        quantity: number = 1,
        channel: TrolleyChannel = 1,
        delivery_method_code: DeliveryMethodCodes,
        withEstimatedTimeDelivery = true
    ) {
      const { addToTrolley } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await addToTrolley(
        product_code,
        quantity,
        channel,
        delivery_method_code,
        withEstimatedTimeDelivery,
      )
    },

    async addToTrolleyForDelivery(code: Product["code"], quantity: number,
                                  withEstimatedTimeDelivery = true) {
      await this.addToTrolley(
          code,
          quantity,
          TrolleyChannel.Delivery,
          DeliveryMethodCodes.Delivery,
          withEstimatedTimeDelivery
      );
    },
    async addToTrolleyForCollection(code: Product["code"], quantity: number, withEstimatedTimeDelivery = true) {
      await this.addToTrolley(
          code,
          quantity,
          TrolleyChannel.Collection,
          DeliveryMethodCodes.Collection,
          withEstimatedTimeDelivery
      );
    },
    async addToTrolleyForDirectship(code: Product["code"], quantity: number, withEstimatedTimeDelivery = true) {
      await this.addToTrolley(
          code,
          quantity,
          TrolleyChannel.Directship,
          DeliveryMethodCodes.Directship,
          withEstimatedTimeDelivery
      );
    },
    async addToTrolleyForNextDayCollection(
        code: Product["code"],
        quantity: number, withEstimatedTimeDelivery = true
    ) {
      await this.addToTrolley(
          code,
          quantity,
          TrolleyChannel.NextDayCollection,
          DeliveryMethodCodes.NextDayCollection,
          withEstimatedTimeDelivery
      );
    },

    /* Unnecessary method - @pending Bulk trolley add method from ecom (dependency) */
    async addMultipleItemsToTrolley(itemsToAdd: TrolleyLineRequest[]) {
      const { addMultipleItemsToTrolley } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await addMultipleItemsToTrolley(itemsToAdd);
    },
    /**
     *
     * @param {string[]} variations - array of product codes received as the variations response from getProducts
     * @returns {Product[]} product variation details with stock info
     */
    async getProductVariants(variations: string[]) {
      const { getVariationsWithStock } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      const variationWithStockResponse = await getVariationsWithStock(variations, this.trolley_id);
      if(!variationWithStockResponse.length) return;
      this.previewed_product_variations = variationWithStockResponse;
      return variationWithStockResponse;
    },

    /**
     * Get trolley lines and channel information for a trolley
     */
    async getTrolleyById(requestTrolleyId = "") {
      const { getTrolleyById } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      return await getTrolleyById(requestTrolleyId);
    },

    async getDecodeTrolleyCodeQueryString(url: string) {
      const { decodeTrolleyDataFromUrl } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      return await decodeTrolleyDataFromUrl(url);
    },

    /**
     * Check if discount is applied to trolley
     */
    async getTrolleyDiscounts() {
      const { checkTrolleyDiscount } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await checkTrolleyDiscount();
    },

    /**
     * Apply Promo Code To Trolley
     */
    async applyPromoCode(code: string = "") {
      const { applyPromoCode } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await applyPromoCode(code);
    },

    /**
     * Remove Applied promo code
     */
    async removeAppliedPromoCode(
        promoCodeId: number,
        showNotifications = true
    ) {
      const { removeAppliedPromoCode } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await removeAppliedPromoCode(promoCodeId, showNotifications);
    },

    /**
     * Remove item from trolley
     * @param {number} line_id
     */
    async removeFromTrolley(line_id: number) {
      if (!line_id) return;
      const { removeFromTrolley } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await removeFromTrolley(line_id);
    },

    /* Quantity change */
    async updateQuantity(line: TrolleyLineItem) {
      if (!line.id || !this.trolley_line_items) return;
      const { updateQuantity } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await updateQuantity(line);
    },

    /* Channel change */
    async updateChannel(line_id: number, channel: TrolleyChannel) {
      if (!this.trolley_line_items) return;
      const { updateChannel } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      await updateChannel(line_id, channel);
    },

    /* Check if channel switch is required based on new branch stock */
    async checkChannelSwitchByBranch(newBranchId: string) {
      if (!this.trolley_line_items) return;
      const { checkChannelSwitchByBranch } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      return await checkChannelSwitchByBranch(newBranchId);
    },

    async updateStatus(options: {
      newStatus: TrolleyStatus;
      targetTrolleyId?: string;
      trolleySessionToken?: string | null;
    }) {
      let trolleyId = this.trolley_id;
      if (options.targetTrolleyId) trolleyId = options.targetTrolleyId;
    
      if (!trolleyId) return;
      const { updateTrolleyStatus } = await import(
        "~/stores-datalayer/trolley.datalayerv2"
      );
      const { newStatus, trolleySessionToken } = options;
      return await updateTrolleyStatus({newStatus, targetTrolleyId: trolleyId, trolleySessionToken});
    },

    async updateActiveTrolleyWithCurrentItems(activeTrolley: TrolleyResponse) {
      let addToTrolleyResponse: any;

      if (!this.trolley_line_items) return;
      // Map the local trolley line items into promises
      const promises = this.all_trolley_line_items.map(async (line) => {
            const { product_code, quantity, channel, delivery_method_code } =
                line;

            const item = {
              product_code,
              quantity,
              channel,
              delivery_method_code,
            };

            /* check if the same product already exists in the active trolley */
            const existingActiveTrolleyLine = activeTrolley.lines.find(
                (activeTrolleyLine) =>
                    activeTrolleyLine.product_code === item.product_code
            );

            console.log(
                "existing active trolley line with same product ->",
                existingActiveTrolleyLine
            );

            // active trolley takes priority, skip adding the same product
            if (existingActiveTrolleyLine) {
              return null;
            }

            /* add the local products in the active trolley */
            return await EcomTrolleyService.addToTrolley(
                activeTrolley.id,
                item,
                activeTrolley.session_token
            );
          });

      const addToTrolleyResponses = await Promise.all(promises);

      // Find the last successful response
      addToTrolleyResponse = addToTrolleyResponses.find(
          (response) => response !== null
      );

      console.log(
          "final add to trolley final response to be set --->",
          addToTrolleyResponse
      );

      if (!addToTrolleyResponse || !addToTrolleyResponse.data) {
        // "No new items were added, setting trolley resource from active trolley."
        await this.setTrolleyResource(activeTrolley);
        return;
      }

      await this.setTrolleyResource(addToTrolleyResponse.data);
    },

    async setTrolleyResource(trolleyData: TrolleyResponse, canSetOrderTotal = true, withEstimatedTimeDelivery = true) {
      this.loading = true;

      try {
        this.storeTrolleyAttributes(trolleyData);

        const promises = [this.setTrolleyLineItems(trolleyData, withEstimatedTimeDelivery)];

        if(canSetOrderTotal) promises.push(this.setOrderTotals());

        await Promise.all(promises);

      } catch (err) {
        const errorMeta = { trolleyStoreError: true };
        const { customClientErrorHandler } = await import("~/services-optimized/customErrorService");
        customClientErrorHandler(
          err,
          "critical",
          "Trolley Store Error - failed to set and process trolley details using setTrolleyResource()",
          errorMeta
        );
        throw err;
      } finally {
        this.loading = false;
      }
    },

    storeTrolleyAttributes(trolleyData: TrolleyResponse) {
      this.trolley.status = trolleyData.status;

      // set trolley attributes from ecom resource
      this.trolley_id = trolleyData.id;
      this.trolley_session_id = trolleyData.session_id;
      if (trolleyData.session_token)
        this.trolley_session_token = trolleyData.session_token;

      this.applied_promo_codes = trolleyData.applied_promo_codes;
    },

    async getActiveCustomerTrolleys(
        customer_id: string,
        customer_token: string
    ) {
      try {
        const {customClientWarningHandler} = await import("~/services-optimized/customErrorService");
        const warningMeta = {customer_id, customer_token, trolleyStoreWarn: true}
        /* get active customer trolley for cross-browser env */
        const res = await EcomCustomerService.fetchActiveCustomerTrolleys(
            customer_id,
            customer_token
        );
        if (!res || !res.data)
         customClientWarningHandler('high', "couldn't fetch active customer trolleys using getActiveCustomerTrolleys", warningMeta);
        return res.data;
      } catch (err) {
        useErrorHandler(err, "critical");
        return [];
      }
    },

    async setActiveCustomerTrolley(
        customer_id: string,
        customer_token: string
    ) {
      const customerActiveTrolleys = await this.getActiveCustomerTrolleys(
          customer_id,
          customer_token
      );
      // 1. if no active trolley exists
      if (!customerActiveTrolleys || !customerActiveTrolleys.length) {
        return false;
      }
      // -----> active trolley set
      const activeTrolley = customerActiveTrolleys[0];
      this.last_active_trolley_id = activeTrolley.id;

      // 2. if both active trolley and local trolley exist
      if (this.trolley_id && this.trolley_id !== activeTrolley.id) {
        const currentTrolleyId = this.trolley_id;
        const currentTrolleySessionToken = this.trolley_session_token;
        // if there is no product in local trolley
        if (this.is_empty) await this.setTrolleyResource(activeTrolley);
        // if there are products in local trolley and active trolley both
        else await this.updateActiveTrolleyWithCurrentItems(activeTrolley);
        // abandon local trolley
        await this.updateStatus({
          newStatus: TrolleyStatus.Abandoned,
          targetTrolleyId: currentTrolleyId,
          trolleySessionToken: currentTrolleySessionToken
        })
        return;
      }
      // 3. if active trolley exists but local trolley doesn't exist
      await this.setTrolleyResource(activeTrolley);
    },

    async getActiveCustomerTrolley(
        customer_id: string,
        customer_token: string
    ) {
      const customerActiveTrolleys = await this.getActiveCustomerTrolleys(
          customer_id,
          customer_token
      );
      // 1. if no active trolley exists
      if (!customerActiveTrolleys || !customerActiveTrolleys.length) {
        return null;
      }
      // -----> active trolley set
      return customerActiveTrolleys[0];
    },

    loadDefaultTrolley() {
      const authStore = useAuthStore();
      const isLoggedIn = authStore.is_authenticated;
      /**
       * Guest user (including guest checkout user) must have trolley `id` and `session_token` to retrieve
       *
       * Auth user must have an associated active trolley in ECOM
       */
      const isTrolleyPresent =
        this.trolley_id &&
        (isLoggedIn || this.trolley_session_token);
    
      if (!isTrolleyPresent) this.destroyTrolley();
    
      /**
       * 1. Set Guest Trolley
       */
      if (!isLoggedIn) {
        import("~/stores-datalayer/trolley.datalayerv2").then(({ setGuestTrolley }) => {
          setGuestTrolley(this);
        })
        return;
      }
      const customerId = authStore.user ? (authStore.user as any).id : null
      const customerToken = authStore.user ? (authStore.user as any).token : null
      /**
       * 2. Set Auth User Trolley
       */
      if (!customerId && !customerToken) return;
      import("~/stores-datalayer/trolley.datalayerv2").then(({ setCustomerTrolley }) => {
        setCustomerTrolley(this, customerId, customerToken);
      })
    },

    findTrolleyItemByProductCode(requiredProductCode: Product["code"]): TrolleyLineItemCore | undefined {
      if (!this.trolley.lines || !this.trolley.lines.length) return;
      return this.trolley.lines.find((line) => line.product_code === requiredProductCode);
    },

    destroyTrolley() {
      this.trolley_id = null;
      this.last_active_trolley_id = null;
      this.trolley_session_id = null;
      this.trolley_session_token = null;
      this.trolley = {} as any;
      this.trolley_line_items = null;
      this.setTotalProducts(null);
    },
  },
});

/* Pinia hot reload */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTrolleyStore, import.meta.hot));
}